import { useState } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  TAGS_CONFIG,
  type Item,
  useTagCategoryList,
  createTag,
  renameTag,
  deleteTag,
  mergeTagInto,
} from "./tagCategory/useTagCategoryList";
import { TagCategoryRow } from "./tagCategory/TagCategoryRow";
import { TagDeleteDialog, TagMergeDialog } from "./tagCategory/TagDialogs";

const TagCategoryList = () => {
  const { active } = useWorkspace();
  const cfg = TAGS_CONFIG;
  const { items, loading, load } = useTagCategoryList(active, cfg);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingMerge, setPendingMerge] = useState<Item | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!active) return;
    setCreating(true);
    const ok = await createTag(active, cfg, newName, items);
    setCreating(false);
    if (ok) { setNewName(""); load(); }
  };

  const startEdit = (item: Item) => { setEditingId(item.id); setEditValue(item.name); };
  const cancelEdit = () => { setEditingId(null); setEditValue(""); };

  const saveEdit = async (item: Item) => {
    setSavingId(item.id);
    const ok = await renameTag(cfg, item, editValue, items);
    setSavingId(null);
    if (ok) { cancelEdit(); load(); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const ok = await deleteTag(cfg, pendingDelete);
    setDeleting(false);
    if (ok) { setPendingDelete(null); load(); }
  };

  const confirmMerge = async () => {
    if (!pendingMerge || !mergeTargetId) return;
    const target = items.find((i) => i.id === mergeTargetId);
    if (!target) return;
    setMerging(true);
    try {
      await mergeTagInto(pendingMerge, mergeTargetId);
      toast.success(`Merged "${pendingMerge.name}" into "${target.name}"`);
      setPendingMerge(null);
      setMergeTargetId(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const createRow = (
    <div className="flex items-center gap-2 pb-3">
      <Input
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }}
        placeholder={`Create new ${cfg.singular}…`}
        className="h-9"
        disabled={creating}
      />
      <Button onClick={handleCreate} disabled={creating || !newName.trim()} size="sm">
        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <>
        {createRow}
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </>
    );
  }

  return (
    <>
      {createRow}
      {!items.length ? (
        <p className="text-sm text-muted-foreground py-4">{cfg.emptyHint}</p>
      ) : (
        <ul className="divide-y">
          {items.map((item) => (
            <TagCategoryRow
              key={item.id}
              item={item}
              isEditing={editingId === item.id}
              isSaving={savingId === item.id}
              editValue={editValue}
              setEditValue={setEditValue}
              onStartEdit={() => startEdit(item)}
              onCancelEdit={cancelEdit}
              onSaveEdit={() => saveEdit(item)}
              onDelete={() => setPendingDelete(item)}
              onMerge={() => { setPendingMerge(item); setMergeTargetId(null); }}
              canMerge={items.length >= 2}
            />
          ))}
        </ul>
      )}

      <TagDeleteDialog
        pendingDelete={pendingDelete}
        deleting={deleting}
        cfg={cfg}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
      <TagMergeDialog
        pendingMerge={pendingMerge}
        items={items}
        mergeTargetId={mergeTargetId}
        setMergeTargetId={setMergeTargetId}
        merging={merging}
        cfg={cfg}
        onCancel={() => { setPendingMerge(null); setMergeTargetId(null); }}
        onConfirm={confirmMerge}
      />
    </>
  );
};

export const TagCategoryManager = () => (
  <Card className="shadow-soft">
    <CardHeader>
      <CardTitle>Tags</CardTitle>
      <p className="text-sm text-muted-foreground">
        Tags are cross-cutting labels (e.g. "Quote", "To-revisit"). Topics are managed in
        Codex → Framework and only appear when the Knowledge Worker Codex is enabled for this
        workspace. Categories live on library items — manage those from the Library Categories card.
      </p>
    </CardHeader>
    <CardContent>
      <TagCategoryList />
    </CardContent>
  </Card>
);
